import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmationService, MessageService, SortEvent } from 'primeng/api';
import { Subscription } from 'rxjs';
import {
  AjusteDeEstoqueDTO,
  AjusteDeEstoqueFormDTO,
  TipoOperacaoDeEstoque,
  TipoContaEstoque,
} from '../../../models/estoque/ajuste-estoque.model';
import { AjusteEstoqueService } from '../../../services/estoque/ajuste-estoque.service';
import { ProdutosService } from '../../../services/produtos/produto.service';
import { TableColumn } from '../../../models/shared/table-column.model';

@Component({
  selector: 'app-ajuste-estoque',
  templateUrl: './ajuste-estoque.component.html',
  styleUrls: ['./ajuste-estoque.component.scss'],
  providers: [MessageService, ConfirmationService],
})
export class AjusteEstoqueComponent implements OnInit, OnDestroy {

  // Dados
  itemId!: number;
  item: any = null;
  ajustes: AjusteDeEstoqueDTO[] = [];
  totalRecords: number = 0;

  // Estado
  loading: boolean = false;
  saving: boolean = false;
  dialogVisible: boolean = false;
  casasDecimaisQuantidade: number = 0;

  // Formulario
  form: AjusteDeEstoqueFormDTO = this.createEmptyForm();

  // Opcoes dos dropdowns
  tiposOperacao: { label: string; value: TipoOperacaoDeEstoque }[] = [];
  tiposConta: { label: string; value: TipoContaEstoque }[] = [];

  // Paginacao
  rows: number = 20;
  first: number = 0;

  pageSizeOptions = [
    { label: 10, value: 10 },
    { label: 20, value: 20 },
    { label: 50, value: 50 },
  ];

  // Busca
  searchTerm: string = '';

  // Ordenacao backend
  lastfield: string = 'id';
  lastSort: string = 'asc';

  // Enums para uso no template
  TipoOperacaoDeEstoque = TipoOperacaoDeEstoque;

  // Configuracao de colunas visiveis
  readonly STORAGE_KEY = 'ajuste-estoque-visible-columns';

  columns: TableColumn[] = [
    { key: 'codigoAjusteDeEstoque', labelKey: 'ajuste_estoque.labels.codigo', visible: true, fixed: true, defaultVisible: true },
    { key: 'tipoOperacao', labelKey: 'ajuste_estoque.labels.tipo_operacao', visible: true, fixed: true, defaultVisible: true },
    { key: 'quantidade', labelKey: 'ajuste_estoque.labels.quantidade', visible: true, fixed: false, defaultVisible: true },
    { key: 'saldoAnterior', labelKey: 'ajuste_estoque.labels.saldo_anterior', visible: true, fixed: false, defaultVisible: true },
    { key: 'saldo', labelKey: 'ajuste_estoque.labels.saldo_posterior', visible: true, fixed: false, defaultVisible: true },
    { key: 'emissao', labelKey: 'ajuste_estoque.labels.data_emissao', visible: true, fixed: false, defaultVisible: true },
    { key: 'acoes', labelKey: 'ajuste_estoque.labels.acoes', visible: true, fixed: false, defaultVisible: true },
  ];

  // i18n - tipo literal implicito (padrao moderno)
  texts = {
    titulo: '',
    historico: '',
    sem_registros: '',
    ajuste_filtros: '',
    carregando: '',
    erro_carregar: '',
  };

  labels = {
    codigo: '',
    tipo_operacao: '',
    quantidade: '',
    saldo_anterior: '',
    saldo_posterior: '',
    data_emissao: '',
    acoes: '',
    entrada: '',
    saida: '',
    tipo_conta: '',
    normal: '',
    ecommerce: '',
    itens_por_pagina: '',
    saldo: '',
  };

  placeholders = {
    pesquisar: '',
  };

  botoes = {
    novo_ajuste: '',
    voltar: '',
    salvar: '',
    cancelar: '',
  };

  tooltips = {
    voltar: '',
    excluir: '',
    botao: '',
  };

  mensagens = {
    confirmar_exclusao: '',
    mensagem_exclusao: '',
    sucesso_criar: '',
    sucesso_deletar: '',
    erro: '',
  };

  private langSub!: Subscription;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private translate: TranslateService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private ajusteEstoqueService: AjusteEstoqueService,
    private produtosService: ProdutosService,
  ) {}

  ngOnInit(): void {
    this.itemId = Number(this.route.snapshot.paramMap.get('itemId'));
    this.loadColumnPreferences();
    this.loadTranslations();
    this.carregarItem();
    this.loadAjustes();

    // Monitorar mudanca de idioma
    this.langSub = this.translate.onLangChange.subscribe(() => {
      this.loadTranslations();
    });
  }

  ngOnDestroy(): void {
    if (this.langSub) {
      this.langSub.unsubscribe();
    }
  }

  // =========================================================================
  // Traducoes
  // =========================================================================

  private loadTranslations(): void {
    this.texts = {
      titulo: this.translate.instant('ajuste_estoque.texts.titulo'),
      historico: this.translate.instant('ajuste_estoque.texts.historico'),
      sem_registros: this.translate.instant('ajuste_estoque.texts.sem_registros'),
      ajuste_filtros: this.translate.instant('ajuste_estoque.texts.ajuste_filtros'),
      carregando: this.translate.instant('ajuste_estoque.texts.carregando'),
      erro_carregar: this.translate.instant('ajuste_estoque.texts.erro_carregar'),
    };

    this.labels = {
      codigo: this.translate.instant('ajuste_estoque.labels.codigo'),
      tipo_operacao: this.translate.instant('ajuste_estoque.labels.tipo_operacao'),
      quantidade: this.translate.instant('ajuste_estoque.labels.quantidade'),
      saldo_anterior: this.translate.instant('ajuste_estoque.labels.saldo_anterior'),
      saldo_posterior: this.translate.instant('ajuste_estoque.labels.saldo_posterior'),
      data_emissao: this.translate.instant('ajuste_estoque.labels.data_emissao'),
      acoes: this.translate.instant('ajuste_estoque.labels.acoes'),
      entrada: this.translate.instant('ajuste_estoque.labels.entrada'),
      saida: this.translate.instant('ajuste_estoque.labels.saida'),
      tipo_conta: this.translate.instant('ajuste_estoque.labels.tipo_conta'),
      normal: this.translate.instant('ajuste_estoque.labels.normal'),
      ecommerce: this.translate.instant('ajuste_estoque.labels.ecommerce'),
      itens_por_pagina: this.translate.instant('ajuste_estoque.labels.itens_por_pagina'),
      saldo: this.translate.instant('ajuste_estoque.labels.saldo'),
    };

    this.placeholders = {
      pesquisar: this.translate.instant('ajuste_estoque.placeholders.pesquisar'),
    };

    this.botoes = {
      novo_ajuste: this.translate.instant('ajuste_estoque.botoes.novo_ajuste'),
      voltar: this.translate.instant('ajuste_estoque.botoes.voltar'),
      salvar: this.translate.instant('ajuste_estoque.botoes.salvar'),
      cancelar: this.translate.instant('ajuste_estoque.botoes.cancelar'),
    };

    this.tooltips = {
      voltar: this.translate.instant('ajuste_estoque.tooltips.voltar'),
      excluir: this.translate.instant('ajuste_estoque.tooltips.excluir'),
      botao: this.translate.instant('ajuste_estoque.tooltips.botao'),
    };

    this.mensagens = {
      confirmar_exclusao: this.translate.instant('ajuste_estoque.mensagens.confirmar_exclusao'),
      mensagem_exclusao: this.translate.instant('ajuste_estoque.mensagens.mensagem_exclusao'),
      sucesso_criar: this.translate.instant('ajuste_estoque.mensagens.sucesso_criar'),
      sucesso_deletar: this.translate.instant('ajuste_estoque.mensagens.sucesso_deletar'),
      erro: this.translate.instant('ajuste_estoque.mensagens.erro'),
    };

    this.buildDropdownOptions();
  }

  // =========================================================================
  // Dropdowns
  // =========================================================================

  private buildDropdownOptions(): void {
    this.tiposOperacao = [
      { label: this.labels.entrada, value: TipoOperacaoDeEstoque.ENTRADA },
      { label: this.labels.saida, value: TipoOperacaoDeEstoque.SAIDA },
    ];

    this.tiposConta = [
      { label: this.labels.normal, value: TipoContaEstoque.NORMAL },
      { label: this.labels.ecommerce, value: TipoContaEstoque.ECOMMERCE },
    ];
  }

  // =========================================================================
  // Carregar dados
  // =========================================================================

  carregarItem(): void {
    this.produtosService.getById('/item/' + this.itemId).subscribe({
      next: (response) => {
        this.item = response;
        // Extrair casas decimais da unidade de medida do item
        this.casasDecimaisQuantidade = (response as any).unidadeMedidaDeItem?.casasDecimais ?? 0;
      },
    });
  }

  loadAjustes(page: number = 0, size: number = this.rows, sort: string = `${this.lastfield},${this.lastSort}`): void {
    this.loading = true;
    this.ajusteEstoqueService.get('/ajusteDeEstoque', {
      page,
      size,
      sort,
      filter: `&itemId=${this.itemId}`,
    }).subscribe({
      next: (response) => {
        this.ajustes = response.content;
        this.totalRecords = response.totalElements;
        this.first = page * size;
        this.loading = false;
      },
      error: () => {
        this.ajustes = [];
        this.totalRecords = 0;
        this.loading = false;
      },
    });
  }

  // =========================================================================
  // Ordenacao backend (customSort)
  // =========================================================================

  customSort(event: SortEvent): void {
    let sort = 'desc';
    if (event.order === 1) {
      sort = 'asc';
    }

    if (this.lastfield === event.field && this.lastSort === sort) {
      return;
    }

    this.lastfield = String(event.field);
    this.lastSort = String(sort);
    const sortStr = `${this.lastfield},${this.lastSort}`;

    this.loadAjustes(0, this.rows, sortStr);
  }

  // =========================================================================
  // Paginacao
  // =========================================================================

  onPageChange(event: any): void {
    const page = event.page;
    const size = event.rows;
    this.rows = size;
    const sort = `${this.lastfield},${this.lastSort}`;
    this.loadAjustes(page, size, sort);
  }

  onRowsPerPageChange(): void {
    this.first = 0;
    const sort = `${this.lastfield},${this.lastSort}`;
    this.loadAjustes(0, this.rows, sort);
  }

  // =========================================================================
  // Busca
  // =========================================================================

  onSearchChange(value: string): void {
    this.searchTerm = value || '';
    // Busca local por codigo ou tipo - o backend nao possui endpoint de filtro por texto
    // Se o backend suportar no futuro, basta adicionar o parametro aqui.
  }

  // =========================================================================
  // Colunas
  // =========================================================================

  isColumnVisible(key: string): boolean {
    const col = this.columns.find(c => c.key === key);
    return col ? col.visible : true;
  }

  get visibleColumnCount(): number {
    return this.columns.filter(c => c.visible).length;
  }

  onColumnsChange(columns: TableColumn[]): void {
    this.columns = [...columns];
  }

  private loadColumnPreferences(): void {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        const visibleKeys = JSON.parse(stored) as string[];
        const storedAll = localStorage.getItem(this.STORAGE_KEY + '-all');
        const allKnownKeys = storedAll
          ? new Set(JSON.parse(storedAll) as string[])
          : new Set(visibleKeys);

        this.columns.forEach(col => {
          if (!col.fixed) {
            if (allKnownKeys.has(col.key)) {
              col.visible = visibleKeys.includes(col.key);
            } else {
              col.visible = col.defaultVisible;
            }
          }
        });
      }
    } catch (e) {
      console.warn('Erro ao carregar preferencias de colunas:', e);
    }
  }

  // =========================================================================
  // Dialog de criacao
  // =========================================================================

  abrirDialog(): void {
    this.form = this.createEmptyForm();
    this.dialogVisible = true;
  }

  fecharDialog(): void {
    this.dialogVisible = false;
    this.form = this.createEmptyForm();
  }

  submit(): void {
    if (!this.form.tipoOperacaoDeEstoque || !this.form.quantidade || this.form.quantidade < 1) {
      return;
    }

    this.saving = true;

    const body: AjusteDeEstoqueDTO = {
      tipoOperacaoDeEstoque: this.form.tipoOperacaoDeEstoque.value,
      item_id: this.itemId,
      quantidade: this.form.quantidade,
      emissao: new Date().toISOString().replace('Z', ''),
      tipoContaEstoque: this.form.tipoContaEstoque?.value || TipoContaEstoque.NORMAL,
    };

    this.ajusteEstoqueService.post('/ajusteDeEstoque', body).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: this.mensagens.sucesso_criar,
        });
        this.fecharDialog();
        this.loadAjustes();
        this.carregarItem();
        this.saving = false;
      },
      error: () => {
        this.saving = false;
      },
    });
  }

  // =========================================================================
  // Exclusao
  // =========================================================================

  confirmarExclusao(ajuste: AjusteDeEstoqueDTO): void {
    this.confirmationService.confirm({
      message: this.mensagens.mensagem_exclusao,
      header: this.mensagens.confirmar_exclusao,
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.deletarAjuste(ajuste.id!);
      },
    });
  }

  private deletarAjuste(id: number): void {
    this.ajusteEstoqueService.delete('/ajusteDeEstoque/' + id).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: this.mensagens.sucesso_deletar,
        });
        this.loadAjustes();
        this.carregarItem();
      },
    });
  }

  // =========================================================================
  // Navegacao
  // =========================================================================

  voltar(): void {
    this.router.navigate(['/produtos']);
  }

  // =========================================================================
  // Utilitarios
  // =========================================================================

  private createEmptyForm(): AjusteDeEstoqueFormDTO {
    return {
      tipoOperacaoDeEstoque: undefined,
      quantidade: undefined,
      tipoContaEstoque: { label: this.labels?.normal || 'Normal', value: TipoContaEstoque.NORMAL },
    };
  }

  formatarData(data: string): string {
    if (!data) return '-';
    const date = new Date(data);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  getTipoOperacaoLabel(tipo: TipoOperacaoDeEstoque): string {
    return tipo === TipoOperacaoDeEstoque.ENTRADA ? this.labels.entrada : this.labels.saida;
  }

  getTipoOperacaoSeverity(tipo: TipoOperacaoDeEstoque): 'success' | 'danger' {
    return tipo === TipoOperacaoDeEstoque.ENTRADA ? 'success' : 'danger';
  }
}
